"use client"

import React, { useState } from 'react'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'
import { UIButton } from '../buttons/ui-button'
import { UITypography } from '../display/ui-typography'
import { UICollapsible, UICollapsibleContent, UICollapsibleTrigger } from '../layout/ui-collapsible'
import { UIBadge } from '../display/ui-badge'
import { ChevronDown, Terminal, CheckCircle2, XCircle, Loader2, Clock } from 'lucide-react'

export interface UIToolExecution {
  id: string
  name: string
  description?: string
  parameters?: Record<string, any>
  result?: any
  error?: string
  status: 'pending' | 'running' | 'success' | 'error'
  duration?: number
  timestamp?: Date
}

export interface UIToolProps {
  // 핵심 데이터
  execution: UIToolExecution

  // UI 옵션
  variant?: 'default' | 'minimal'
  showParameters?: boolean
  showResult?: boolean
  showDuration?: boolean
  isCollapsed?: boolean
  defaultCollapsed?: boolean
  onCollapsedChange?: (collapsed: boolean) => void

  // 스타일링
  className?: string

  // 접근성 및 i18n
  ariaLabel?: string
  locale?: 'ko' | 'en'

  // 콜백
  onRetry?: () => void
}

/**
 * UITool - 도구 호출을 표시하는 컴포넌트
 * shadcn.io/ai Tool 컴포넌트 기반으로 구현
 */
export const UITool = React.forwardRef<HTMLDivElement, UIToolProps>(
  ({
    execution,
    variant = 'default',
    showParameters = true,
    showResult = true,
    showDuration = true,
    isCollapsed: controlledCollapsed,
    defaultCollapsed = false,
    onCollapsedChange,
    className,
    ariaLabel,
    locale = 'ko',
    onRetry,
    ...props
  }, ref) => {
    const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed)
    const [isPressed, setIsPressed] = useState(false)

    const isControlled = controlledCollapsed !== undefined
    const collapsed = isControlled ? controlledCollapsed : internalCollapsed

    const handleCollapsedChange = (value: boolean) => {
      if (!isControlled) {
        setInternalCollapsed(value)
      }
      onCollapsedChange?.(value)
    }

    const statusConfig = {
      pending: {
        icon: Clock,
        color: 'text-yellow-600 dark:text-yellow-400',
        bgColor: 'bg-amber-50/30 dark:bg-amber-950/20',
        borderColor: 'border-yellow-200/[0.30] dark:border-yellow-400/[0.40]',
        shadowColor: 'shadow-yellow-500/[0.15] dark:shadow-yellow-400/[0.35]',
        label: locale === 'ko' ? '대기 중' : 'Pending'
      },
      running: {
        icon: Loader2,
        color: 'text-blue-600 dark:text-blue-400',
        bgColor: 'bg-blue-50/30 dark:bg-blue-950/20',
        borderColor: 'border-blue-200/[0.30] dark:border-blue-400/[0.40]',
        shadowColor: 'shadow-blue-500/[0.15] dark:shadow-blue-400/[0.35]',
        label: locale === 'ko' ? '실행 중' : 'Running'
      },
      success: {
        icon: CheckCircle2,
        color: 'text-green-800 dark:text-green-400',
        bgColor: 'bg-green-50/30 dark:bg-green-950/20',
        borderColor: 'border-green-200/[0.30] dark:border-green-400/[0.40]',
        shadowColor: 'shadow-green-500/[0.15] dark:shadow-green-400/[0.35]',
        label: locale === 'ko' ? '성공' : 'Success'
      },
      error: {
        icon: XCircle,
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-50/30 dark:bg-red-950/20',
        borderColor: 'border-red-200/[0.30] dark:border-red-400/[0.40]',
        shadowColor: 'shadow-red-500/[0.15] dark:shadow-red-400/[0.35]',
        label: locale === 'ko' ? '오류' : 'Error'
      }
    }

    const config = statusConfig[execution.status]
    const StatusIcon = config.icon

    if (variant === 'minimal') {
      return (
        <UIBox
          ref={ref}
          className={cn(
            "inline-flex items-center gap-2 px-3 py-1.5 rounded-lg",
            "shadow-sm transition-all duration-300 ease-out border",
            config.bgColor,
            config.borderColor,
            config.shadowColor,
            className
          )}
          aria-label={ariaLabel || `${execution.name} ${config.label}`}
          {...props}
        >
          <StatusIcon className={cn(
            "h-3 w-3 transition-all duration-300 ease-out",
            "drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]",
            config.color,
            execution.status === 'running' && "animate-spin"
          )} />
          <UITypography
            variant="caption"
            weight="medium"
            className="drop-shadow-sm leading-relaxed"
          >
            {execution.name}
          </UITypography>
          {showDuration && execution.duration && (
            <UITypography
              variant="caption"
              className="text-muted-foreground drop-shadow-sm"
            >
              ({execution.duration}ms)
            </UITypography>
          )}
        </UIBox>
      )
    }

    return (
      <UIBox
        ref={ref}
        className={cn(
          // Apple glassmorphism base
          "rounded-xl transition-all duration-300 ease-out border",
          // Status-specific styling
          config.bgColor,
          config.borderColor,
          config.shadowColor,
          // Enhanced hover effects with lift
          "hover:-translate-y-0.5 hover:scale-[1.002] hover:shadow-2xl",
          className
        )}
        aria-label={ariaLabel || `${execution.name} ${config.label}`}
        {...props}
      >
        <UICollapsible
          open={!collapsed}
          onOpenChange={(open) => handleCollapsedChange(!open)}
        >
          <UICollapsibleTrigger asChild>
            <button
              className={cn(
                "w-full flex items-center justify-between px-4 py-3 text-sm transition-all duration-300 ease-out",
                "rounded-lg focus:outline-none",
                // Apple-style hover: subtle brightness increase
                "hover:brightness-105 hover:bg-white/[0.015] dark:hover:bg-white/[0.01]",
                // 개선된 클릭 상태 - 자연스러운 오렌지 강조
                isPressed && "bg-accent/[0.08] border-accent/20 shadow-sm",
                // Subtle active scale
                "active:scale-[0.99] active:transition-transform active:duration-150"
              )}
              onMouseDown={() => {
                setIsPressed(true)
                // 자연스러운 타이밍으로 pressed 상태 해제
                setTimeout(() => {
                  setIsPressed(false)
                }, 200)
              }}
              onMouseUp={() => {
                // 마우스업 시 포커스 제거 (키보드 접근성은 유지)
                setTimeout(() => {
                  const target = document.activeElement as HTMLElement
                  if (target && target.tagName === 'BUTTON') {
                    target.blur()
                  }
                }, 100)
              }}
            >
              <UIBox className="flex items-center gap-3">
                <Terminal className={cn(
                  "h-4 w-4 transition-all duration-300 ease-out",
                  "drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]",
                  collapsed ? "text-foreground/90" : "text-foreground/85"
                )} />
                <UIBox className="flex flex-col items-start">
                  <UITypography
                    variant="body2"
                    weight="medium"
                    className={cn(
                      "transition-colors duration-300 ease-out leading-relaxed",
                      "drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]",
                      "text-slate-900"
                    )}
                  >
                    {execution.name}
                  </UITypography>
                  {execution.description && (
                    <UITypography
                      variant="caption"
                      className={cn(
                        "transition-colors duration-300 ease-out leading-relaxed",
                        "drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]",
                        collapsed ? "text-foreground/90" : "text-foreground/85"
                      )}
                    >
                      {execution.description}
                    </UITypography>
                  )}
                </UIBox>
              </UIBox>
              <UIBox className="flex items-center gap-3">
                <StatusIcon className={cn(
                  "h-4 w-4 transition-all duration-300 ease-out",
                  "drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]",
                  // 접힌 상태에서만 상태별 색상 표시, 펼쳐진 상태에서는 중성색
                  collapsed ? config.color : "text-foreground/90",
                  execution.status === 'running' && "animate-spin"
                )} />
                <UIBadge
                  variant={collapsed && execution.status === 'error' ? 'destructive' : 'secondary'}
                  size="sm"
                  className={cn(
                    "transition-all duration-300 ease-out",
                    collapsed
                      ? "bg-white/[0.06] border-white/[0.12]"
                      : "bg-white/[0.03] border-white/[0.08]"
                  )}
                >
                  {config.label}
                </UIBadge>
                <ChevronDown className={cn(
                  "h-4 w-4 transition-all duration-300 ease-out",
                  "drop-shadow-sm dark:drop-shadow-[0_1px_2px_rgba(0,0,0,0.4)]",
                  "text-foreground/85",
                  !collapsed && "rotate-180"
                )} />
              </UIBox>
            </button>
          </UICollapsibleTrigger>

          <UICollapsibleContent className="transition-all duration-300 ease-out data-[state=closed]:animate-out data-[state=closed]:fade-out-50 data-[state=open]:animate-in data-[state=open]:fade-in-50">
            <UIBox className="px-4 pb-4 space-y-4">
              {/* 파라미터 */}
              {showParameters && execution.parameters && Object.keys(execution.parameters).length > 0 && (
                <UIBox>
                  <UITypography
                    variant="caption"
                    weight="medium"
                    className="text-slate-900 mb-1 drop-shadow-sm leading-relaxed"
                  >
                    {locale === 'ko' ? '파라미터' : 'Parameters'}
                  </UITypography>
                  <UIBox className="bg-muted/50 dark:bg-muted/20 rounded-lg p-3 font-mono text-xs border border-white/[0.08] dark:border-white/[0.06] shadow-sm">
                    <pre
                      className="overflow-x-auto focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
                      tabIndex={0}
                      role="region"
                      aria-label={locale === 'ko' ? `${execution.name} 파라미터 코드` : `${execution.name} parameters code`}
                    >
                      {JSON.stringify(execution.parameters, null, 2)}
                    </pre>
                  </UIBox>
                </UIBox>
              )}

              {/* 결과 */}
              {showResult && execution.result && (
                <UIBox>
                  <UITypography
                    variant="caption"
                    weight="medium"
                    className="text-slate-900 mb-1 drop-shadow-sm leading-relaxed"
                  >
                    {locale === 'ko' ? '결과' : 'Result'}
                  </UITypography>
                  <UIBox className="bg-muted/50 dark:bg-muted/20 rounded-lg p-3 font-mono text-xs border border-white/[0.08] dark:border-white/[0.06] shadow-sm">
                    <pre
                      className="overflow-x-auto focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded"
                      tabIndex={0}
                      role="region"
                      aria-label={locale === 'ko' ? `${execution.name} 결과 코드` : `${execution.name} result code`}
                    >
                      {typeof execution.result === 'string'
                        ? execution.result
                        : JSON.stringify(execution.result, null, 2)
                      }
                    </pre>
                  </UIBox>
                </UIBox>
              )}

              {/* 오류 */}
              {execution.error && (
                <UIBox>
                  <UITypography
                    variant="caption"
                    weight="medium"
                    className="text-destructive mb-1 drop-shadow-sm leading-relaxed"
                  >
                    {locale === 'ko' ? '오류' : 'Error'}
                  </UITypography>
                  <UIBox className="bg-red-50/60 dark:bg-red-950/25 border border-red-200/[0.15] dark:border-red-400/[0.20] rounded-lg p-3 shadow-sm shadow-red-500/[0.08] dark:shadow-red-400/[0.25]">
                    <UITypography
                      variant="body2"
                      className="text-destructive leading-relaxed drop-shadow-sm"
                    >
                      {execution.error}
                    </UITypography>
                  </UIBox>
                  {onRetry && (
                    <UIButton
                      variant="outline"
                      size="sm"
                      onClick={onRetry}
                      className="mt-2"
                    >
                      {locale === 'ko' ? '재시도' : 'Retry'}
                    </UIButton>
                  )}
                </UIBox>
              )}

              {/* 메타데이터 */}
              <UIBox className="flex items-center gap-4 text-xs text-slate-600">
                {showDuration && execution.duration && (
                  <UITypography
                    variant="caption"
                    className="text-slate-600 drop-shadow-sm leading-relaxed"
                  >
                    {locale === 'ko' ? '실행 시간' : 'Duration'}: {execution.duration}ms
                  </UITypography>
                )}
                {execution.timestamp && (
                  <UITypography
                    variant="caption"
                    className="text-slate-600 drop-shadow-sm leading-relaxed"
                  >
                    {execution.timestamp.toLocaleTimeString()}
                  </UITypography>
                )}
              </UIBox>
            </UIBox>
          </UICollapsibleContent>
        </UICollapsible>
      </UIBox>
    )
  }
)

UITool.displayName = "UITool"